import React, { useEffect, useState } from "react";
import { appColors, appPrimaryColor } from "../utils/constants";
import FarmerInviteStatusButton from "../components/microComponents/FarmerInviteStatusButton";
import SurveyProgressBar from "../components/microComponents/SurveyProgressBar";
import InnerLoader from "../components/microComponents/InnerLoader";
import RightArrow from "../assets/svg/RightArrow";
import { deleteFarmer, getOrgInviteDetails } from "../api";
import { toast } from "react-toastify";
import AddUserModal from "../components/modal/AddUserModal";
import { BiSearch } from "react-icons/bi";
import { IoMdAdd } from "react-icons/io";
import { getUser, handleFloatInt } from "../utils";
import { AiOutlineDelete } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import OrganizationSelector from "../components/microComponents/OrganizationSelector";

const Farmers = () => {
    const [searchValue, setSearchValue] = useState("");
    const [farmersList, setFarmersList] = useState([]);
    // console.log("🚀 ~ Farmers ~ farmersList:", farmersList)
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loader, setLoader] = useState(false);
    const [detailsPageNumber, setDetailsPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const barHeight = "h-6";
    const isAdmin = getUser()?.role === 'admin';
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [farmerToDelete, setFarmerToDelete] = useState(null);
    const navigate = useNavigate();
    const { user, selectedOrg } = useApp();
    const isSuperAdmin = user?.role === 'super_admin';

    const openModal = () => {
        setIsModalOpen(true)
        setSearchValue('')
    };
    const closeModal = () => {
        setIsModalOpen(false)
        setSearchValue('')
        setDetailsPageNumber(1)
        setLoader(true)
        getFarmerDetails()
    };

    const getFarmerDetails = async () => {
        try {
            const response = await getOrgInviteDetails(searchValue, detailsPageNumber, selectedOrg === null ? '' : selectedOrg);
            // console.log("🚀 ~ getFarmerDetails ~ response:", response)
            setFarmersList(response?.data?.invite_details ?? []);
            setTotalPages(response?.data?.total_pages ?? 1);
        } catch (error) {
            console.error("Error fetching farmer details:", error);
            toast.error("Error fetching farmer details");
        } finally {
            setLoader(false);
        }
    };

    const [debounceTimer, setDebounceTimer] = useState(0);

    const debounce = (callback, delay = 400) => {
        if (debounceTimer) clearTimeout(debounceTimer);
        setDebounceTimer(setTimeout(callback, delay));
    };

    useEffect(() => {
        setLoader(true);
        debounce(() => getFarmerDetails(), 700);
    }, [detailsPageNumber, searchValue, selectedOrg]);

    useEffect(() => {
        setDetailsPageNumber(1);
    }, [searchValue]);

    const handleDeleteFarmer = (farmer) => {
        setFarmerToDelete(farmer);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        // Loader
        setLoader(true);

        // TODO: Implement actual delete API call here
        // console.log("Deleting farmer:", farmerToDelete?._id, farmerToDelete?.email);

        deleteFarmer(farmerToDelete?._id).then((response) => {
            // console.log("🚀 ~ deleteFarmer ~ response:", response)
            // Close modal and reset farmer
            setIsDeleteModalOpen(false);
            setFarmerToDelete(null);
            if (response.status === 200) {
                // Show success message
                toast.success("Farmer deleted successfully");
                getFarmerDetails();
            } else {
                toast.error("There was an error deleting the farmer. Please try again.");
                setLoader(false);
            }
        }).catch((error) => {
            console.error("Error deleting farmer:", error);
            toast.error("There was an error deleting the farmer. Please try again.");
            setLoader(false);
        })
    };

    return (
        <div className="flex flex-col gap-6 max-w-[1400px] mx-auto">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-xl shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Farmers Management</h1>
                    <p className="text-gray-500 mt-1">Manage and monitor your farmer network</p>
                </div>
                <div className="flex items-center gap-4">
                    {isAdmin && (
                        <button
                            onClick={openModal}
                            className={`${appPrimaryColor} px-6 py-2.5 text-white rounded-lg hover:opacity-90 transition-all flex items-center gap-2`}
                        >
                            <IoMdAdd size={20} />
                            <span>Add Farmer</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                {/* Search and Filters */}
                <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center mb-6">
                    <div className="relative w-full lg:w-96 flex flex-row justify-center items-center gap-4">
                        <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="search"
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-darkGreen"
                            placeholder="Search farmers..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                        {loader && <InnerLoader loaderSize={{ width: "30px", height: "30px" }}
                            divSize={{ width: "30px", height: "30px" }}
                        />}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center gap-2 text-sm">
                        {isSuperAdmin && <div className="flex justify-center items-center border pr-2 rounded-lg border-[#e5e7eb] relative">
                            <OrganizationSelector className="border-none" />
                        </div>
                        }
                        <button
                            onClick={() => setDetailsPageNumber(prev => prev === 1 ? 1 : prev - 1)}
                            className="p-2 rounded-lg border hover:bg-gray-50 transition-colors disabled:opacity-50"
                            disabled={detailsPageNumber === 1}
                        >
                            <RightArrow width={14} height={14} className="rotate-180" />
                        </button>
                        <span className="px-4 py-2 border rounded-lg">
                            Page {detailsPageNumber} of {totalPages === 0 ? 1 : totalPages}
                        </span>
                        <button
                            onClick={() => detailsPageNumber < totalPages ? setDetailsPageNumber(prev => prev + 1) : null}
                            className="p-2 rounded-lg border hover:bg-gray-50 transition-colors disabled:opacity-50"
                            disabled={detailsPageNumber >= totalPages}
                        >
                            <RightArrow width={14} height={14} />
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50 text-left">
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 rounded-tl-lg">Name</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 text-center">Email</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 text-center">Organization</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 text-center">Status</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 text-center">Progress</th>
                                {<th className="px-6 py-3 text-sm font-semibold text-gray-600 rounded-tr-lg text-center">Actions</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loader ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-8"></td>
                                </tr>
                            ) : farmersList?.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-8 text-gray-500">
                                        No farmers found
                                    </td>
                                </tr>
                            ) : (
                                farmersList?.map((farmer, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-50 transition-colors"
                                    >
                                        <td className="px-6 py-4 cursor-pointer"
                                            onClick={() => navigate(`/about-farmer/${farmer?.user_id}`)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-darkGreen text-white flex items-center justify-center uppercase">
                                                    {farmer?.first_name?.[0] ?? '-'}
                                                </div>
                                                <span className="font-medium">
                                                    {farmer?.first_name && farmer?.last_name ? farmer?.first_name + " " + farmer?.last_name : '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 cursor-pointer"
                                            onClick={() => navigate(`/about-farmer/${farmer?.user_id}`)}
                                        >{farmer?.email ?? '-'}</td>
                                        <td className="px-6 py-4 text-gray-600 cursor-pointer text-center"
                                            onClick={() => navigate(`/about-farmer/${farmer?.user_id}`)}
                                        >{farmer?.org_name ? farmer?.org_name : '-'}</td>
                                        <td className="px-6 py-4 cursor-pointer"
                                            onClick={() => navigate(`/about-farmer/${farmer?.user_id}`)}
                                        >
                                            <FarmerInviteStatusButton status={farmer?.status ?? '-'} barHeight={barHeight} />
                                        </td>
                                        <td className="px-2 py-4 cursor-pointer text-center"
                                            onClick={() => navigate(`/about-farmer/${farmer?.user_id}`)}
                                        >
                                            {farmer?.survey_completion ? (
                                                <SurveyProgressBar barHeight={barHeight} percentage={handleFloatInt(farmer?.survey_completion ?? 0)} />
                                            ) : (
                                                <span className="text-gray-400">-</span>
                                            )}
                                        </td>
                                        <td className="py-4 text-center">
                                            <button
                                                className="text-red-500 hover:text-red-700 transition-colors p-2 rounded-full hover:bg-red-50"
                                                onClick={() => handleDeleteFarmer(farmer)}
                                            >
                                                <AiOutlineDelete size={20} color={appColors.secondaryRed} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddUserModal isOpen={isModalOpen} onRequestClose={closeModal} />

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

                    {/* Modal Container */}
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                            {/* Modal Content */}
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Confirm Delete</h2>
                                <p className="mt-2 text-sm text-gray-500">
                                    Are you sure you want to delete {farmerToDelete?.first_name} {farmerToDelete?.last_name}? This action cannot be undone.
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-4">
                                <button
                                    onClick={() => {
                                        setIsDeleteModalOpen(false);
                                        setFarmerToDelete(null);
                                    }}
                                    className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-gray-300
                                        text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 
                                        focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className={`w-full sm:w-auto px-6 py-2.5 rounded-lg text-white bg-secondaryRed hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors ${loader ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={loader}
                                >
                                    {loader ? "Deleting..." : "Delete Farmer"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Farmers;
